<template>
  <TableSimple :data="tables"></TableSimple>
</template>

<script>
import { formatNumber } from "@/plugins/utils";
import plur from "plur";

export default {
  name: "NodalPCTabular",
  components: {
    TableSimple: () => import("@/components/visualizations/TableSimple.vue"),
  },
  mixins: [],
  props: {
    selectedTab: { type: String, required: true, default: "nodal" },
    caseOutputs: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    inputCase: { type: Object, required: false, default: () => ({}) },
    horizontal: { type: Boolean, required: false, default: false },
    selectedNodeId: { type: Object, required: true, default: () => ({ nodeId: 1 }) },
  },
  data() {
    return {
      selectedItem: "",
      selectedYear: null,
    };
  },
  computed: {
    caseInputs() {
      return this.inputCase.input;
    },

    caseInputYears() {
      const years = [];
      for (
        let i = this.inputCase.settings.year_start;
        i <= this.inputCase.settings.year_end;
        i += 1
      )
        years.push(i);
      return years;
    },

    caseYears() {
      if (this.caseInputYears.length > this.simulationYearArray.length) return this.caseInputYears;
      return this.simulationYearArray;
    },

    yearlyDemandTable() {
      const demands =
        this.selectedTab === "systemLevel"
          ? {
              "System Demand (GWh)": Object.values(this.yearlyOutputs).map(
                (yearlyResult) => yearlyResult.system[0].energyDemand
              ),
              "Peak Demand (MW)": Object.values(this.yearlyOutputs).map(
                (yearlyResult) => yearlyResult.system[0].peakLoad
              ),
              "Energy (GWh)": Object.values(this.yearlyOutputs).map((yearlyResult) =>
                yearlyResult.demands.reduce((acc, demand) => acc + demand.servedDemand / 1000, 0)
              ),
              "Lost Energy (GWh)": Object.values(this.yearlyOutputs).map(
                (yearlyResult) => yearlyResult.system[0].lostLoad
              ),
            }
          : {
              "Nodal Demand (GWh)": Object.values(this.yearlyOutputs).map(
                (yearlyResult) => yearlyResult.nodes[0].energyDemand
              ),
              "Peak Demand (MW)": Object.values(this.yearlyOutputs).map(
                (yearlyResult) => yearlyResult.nodes[0].peakLoad
              ),
              "Energy (GWh)": Object.values(this.yearlyOutputs).map((yearlyResult) =>
                yearlyResult.demands.reduce((acc, demand) => acc + demand.servedDemand / 1000, 0)
              ),
              "Lost Energy (GWh)": Object.values(this.yearlyOutputs).map(
                (yearlyResult) => yearlyResult.nodes[0].lostLoad
              ),
            };
      return {
        formatHeader: false,
        header: ["Demand", ...this.simulationYearArray],
        rows: Object.entries(demands).reduce((acc, [title, values]) => {
          return [...acc, [title, ...values]];
        }, []),
      };
    },

    yearlyGeneratorCapacityTable() {
      const capacitiesObject = {};
      const storageCapacity = [];
      this.simulationYearArray.forEach((currYear) => {
        const yearIdx = currYear - this.inputCase.settings.year_start;

        Object.entries(
          this.caseInputs.generators
            .filter(
              (generator) =>
                this.selectedTab === "systemLevel" ||
                (this.selectedTab === "nodal" && generator.bus_id === this.selectedNodeId.nodeId)
            )
            .reduce((acc, val) => {
              if (!val.active) return acc;
              return { ...acc, [val.source]: (acc[val.source] || 0) + val.capacity[yearIdx] };
            }, {})
        ).forEach(([key, val], _) => {
          capacitiesObject[key] = [...(capacitiesObject[key] || []), val];
        });

        storageCapacity.push(
          this.caseInputs.storage
            .filter((storage) => storage.active)

            .map((storage) => storage.power_capacity[yearIdx])
            .reduce((acc, val) => acc + val, 0)
        );
      });

      return {
        formatHeader: false,
        header: ["Capacity (INPUT)", ...this.simulationYearArray],
        rows: [
          ...Object.entries(capacitiesObject).map(([title, value], _) => [
            `${title} (MW)`,
            ...value,
          ]),
          ...(storageCapacity
            .filter((storage) => storage.active)
            .filter(
              (storage) =>
                this.selectedTab === "systemLevel" ||
                (this.selectedTab === "nodal" && storage.bus_id === this.selectedNodeId.nodeId)
            ).length
            ? [["STORAGE (MW)", ...storageCapacity]]
            : []),
        ],
      };
    },

    generatorTypes() {
      return this.allGeneratorOutputs(Object.keys(this.yearlyOutputs).slice(-1)).reduce(
        (types, generator) => {
          const fuelType = this.caseInputs.generators.find((gen) => gen.id === generator.id).source;

          return { ...types, [generator.id]: fuelType };
        },
        {}
      );
    },

    yearlyGenerationPerFuelTypesTable() {
      const rows = Object.entries(this.yearlyFuelTypeData("generation")).map(([key, values], _) => {
        return [`${key} (GWh)`, ...values.map((v) => v / 1000)];
      });
      const total = Object.entries(this.yearlyFuelTypeData("generation"))
        .map(([, values], _) => {
          return values.map((v) => v / 1000);
        })
        .reduce(
          (acc, array) => acc.map((sum, i) => sum + array[i]),
          Array(this.simulationYearArray.length).fill(0)
        );
      return {
        formatHeader: false,
        lastRowBold: true,
        header: ["Energy by Fuel Type (OUTPUT)", ...this.simulationYearArray],
        rows: [...rows, ["Total (GWh)", ...total]],
      };
    },
    yearlyStorageTable() {
      // If there is no storages in the current node, we don't show data.
      if (this.noStorages) return null;
      const discharge = Object.values(this.yearlyOutputs).map((yearlyResult) =>
        yearlyResult.storages.reduce(
          (acc, storageResult) => acc + storageResult.discharge / 1000,
          0
        )
      );
      const charge = Object.values(this.yearlyOutputs).map((yearlyResult) =>
        yearlyResult.storages.reduce((acc, storageResult) => acc + storageResult.charge / 1000, 0)
      );
      const net = [discharge, charge.map((v) => -1 * v)].reduce(
        (acc, array) => acc.map((sum, i) => sum + array[i]),
        Array(discharge.length).fill(0)
      );
      return {
        formatHeader: false,
        header: ["Storage (OUTPUT)", ...this.simulationYearArray],
        rows: [
          ["Charge (GWh)", ...charge],
          ["Discharge (GWh)", ...discharge],
          ["Net Energy (GWh)", ...net],
        ],
        lastRowBold: true,
      };
    },

    yearlyTotalGenerationCostByFuelTable() {
      const generationCosts = this.yearlyFuelTypeData("generationCost");
      return {
        header: ["Total Generation Costs (OUTPUT)", ...this.simulationYearArray],
        rows: [
          ...Object.entries(generationCosts).reduce((acc, [title, values]) => {
            return [...acc, [`${title} ($)`, ...values]];
          }, []),
          [
            "TOTAL ($)",
            ...Object.keys(this.simulationYearArray).map((_, idx) => {
              return Object.values(generationCosts).reduce((sum, val) => {
                if (val[idx] !== "-") return sum + val[idx];
                return sum;
              }, 0);
            }),
          ],
        ],
        lastRowBold: true,
      };
    },

    yearlyGenerationByNodeTable() {
      return this.yearlyTableData((generation, _) => generation, "Generation by Node (OUTPUT)");
    },
    yearlyDemandByNodeTable() {
      return this.yearlyTableData((_, demand) => demand, "Demand by Node (OUTPUT)");
    },
    yearlyFuelPricesTable() {
      if (this.noThermalGenerators) return null;
      const filterSources = ["SOLAR", "WIND", "HYDRO"];
      const generatorFuelPricesBySource = this.caseInputs.generators
        .filter(
          (generator) =>
            this.selectedTab === "systemLevel" ||
            (this.selectedTab === "nodal" && generator.bus_id === this.selectedNodeId.nodeId)
        )
        .reduce((acc, val) => {
          if (filterSources.some((str) => val.source.includes(str))) return acc;
          acc[val.source] = [...(acc[val.source] || []), val.fuel_price];
          return acc;
        }, {});

      const fuelPricesObject = {};
      this.simulationYearArray.forEach((currYear) => {
        const yearIdx = currYear - this.inputCase.settings.year_start;

        Object.entries(generatorFuelPricesBySource).forEach(([fuelType, fuelPrices]) => {
          const avgFuelPriceOfTypeForYear =
            fuelPrices.reduce((acc, val) => {
              return acc + val[yearIdx];
            }, 0) / generatorFuelPricesBySource[fuelType].length;

          fuelPricesObject[fuelType.concat(" ($/MMBTU)")] = [
            ...(fuelPricesObject[fuelType.concat(" ($/MMBTU)")] || []),
            avgFuelPriceOfTypeForYear,
          ];
        });
      });

      return {
        formatHeader: false,
        header: [`Fuel Prices (INPUT)`, ...this.simulationYearArray],
        rows: Object.entries(fuelPricesObject).map(([key, values], _) => {
          return [key, ...values];
        }),
      };
    },

    yearlyEmmissionsTable() {
      if (this.noThermalGenerators) return null;
      return {
        header: ["Emissions (OUTPUT)", ...this.simulationYearArray],
        rows: [
          ...Object.entries(
            this.simulationYearArray.reduce(
              (yearlyAcc, year) => {
                if (this.allGeneratorOutputs(year).length === 0) {
                  yearlyAcc["CO2 (ton)"].push("-");
                  yearlyAcc["Emissions Cost ($)"].push("-");
                } else {
                  const emmissions = this.allGeneratorOutputs(year).reduce(
                    (acc, val) => {
                      acc["CO2 (ton)"] += val.emission;
                      acc["Emissions Cost ($)"] += val.emissionCost;
                      return acc;
                    },
                    { "CO2 (ton)": 0, "Emissions Cost ($)": 0 }
                  );

                  yearlyAcc["CO2 (ton)"].push(emmissions["CO2 (ton)"]);
                  yearlyAcc["Emissions Cost ($)"].push(emmissions["Emissions Cost ($)"]);
                }
                return yearlyAcc;
              },
              { "CO2 (ton)": [], "Emissions Cost ($)": [] }
            )
          ).map(([key, val], _) => {
            return [key, ...val];
          }),
        ],
      };
    },

    computedSelectedNodeId: {
      get() {
        return this.selectedNodeId;
      },
      set(val) {
        this.$emit("update:selectedNodeId", val);
        this.$emit("change");
      },
    },
    nodes() {
      return this.inputCase.input.nodes.map((el) => ({
        name: el.name,
        id: el.id,
      }));
    },
    resourcesWithOutput() {
      return Object.keys(Object.values(this.yearlyOutputs)[0]).filter(
        (field) => Object.values(this.yearlyOutputs)[0][field].length
      );
    },
    yearlyOutputs() {
      return Object.entries(this.caseOutputs.yearlyResults).reduce(
        (filteredYearResults, [year, objects]) => {
          const filteredBySelectedNode = Object.entries(objects).reduce(
            (filteredAsset, [assetString, assetArray]) => {
              return {
                ...filteredAsset,
                [assetString]: assetArray.filter(
                  (asset) =>
                    this.selectedTab === "systemLevel" ||
                    (this.selectedTab === "nodal" && asset.nodeID === this.selectedNodeId.nodeId)
                ),
              };
            },
            {}
          );
          return { ...filteredYearResults, [year]: filteredBySelectedNode };
        },
        {}
      );
    },
    simulationYearArray() {
      return Object.keys(this.yearlyOutputs);
    },
    mappingSystemLevel() {
      return {
        demands: [
          {
            title: "Demand",
            format: 0,
            item: { resource: "demands", key: "demand" },
            suffix: "MWh",
          },
        ],
        storages: [],
        branches: [],
        nodes: [],
        markets: [],
        hydroGenerators: [
          {
            title: "Generation",
            format: 0,
            item: { resource: "hydroGenerators", key: "discharge" },
            suffix: "MWh",
          },
          {
            title: "Total Cost",
            format: 0,
            item: { resource: "hydroGenerators", key: "totCost" },
            suffix: "$/yr",
          },
        ],
        renewableGenerators: [
          {
            title: "Generation",
            format: 0,
            item: { resource: "renewableGenerators", key: "discharge" },
            suffix: "Mwh",
          },
          {
            title: "Total Cost",
            format: 0,
            item: { resource: "renewableGenerators", key: "totCost" },
            suffix: "$/yr",
          },
        ],
        thermalGenerators: [
          {
            title: "Generation",
            format: 0,
            item: { resource: "thermalGenerators", key: "discharge" },
            suffix: "Mwh",
          },
          {
            title: "Total Cost",
            format: 0,
            item: { resource: "thermalGenerators", key: "totCost" },
            suffix: "$/yr",
          },
        ],
      };
    },
    yearlyValues() {
      return this.simulationYearArray.map((year) => ({
        year,
        values: this.mappingSystemLevel[this.resourcesWithOutput[0]].map((el) => ({
          title: el.title,
          suffix: el.suffix,
          totalValue: this.yearlyOutputs[year][el.item.resource],
          value: Object.values(this.yearlyOutputs[year][el.item.resource]).find(
            (resource) => resource.name === this.selectedItem
          )
            ? formatNumber(
                Object.values(this.yearlyOutputs[year][el.item.resource]).find(
                  (resource) => resource.name === this.selectedItem
                )[el.item.key],
                0
              )
            : formatNumber(
                Object.values(this.yearlyOutputs[year][el.item.resource])[0][el.item.key],
                0
              ),
        })),
      }));
    },
    noStorages() {
      return !this.caseInputs.storage.filter(
        (storage) =>
          this.selectedTab === "systemLevel" ||
          (this.selectedTab === "nodal" && storage.bus_id === this.selectedNodeId.nodeId)
      ).length;
    },
    noGenerators() {
      return !this.caseInputs.generators.filter(
        (generator) =>
          this.selectedTab === "systemLevel" ||
          (this.selectedTab === "nodal" && generator.bus_id === this.selectedNodeId.nodeId)
      ).length;
    },
    noThermalGenerators() {
      return !this.caseInputs.generators
        .filter((generator) => generator.type === "THERMAL")
        .filter(
          (generator) =>
            this.selectedTab === "systemLevel" ||
            (this.selectedTab === "nodal" && generator.bus_id === this.selectedNodeId.nodeId)
        ).length;
    },
    tables() {
      return [
        { table: this.yearlyDemandTable, format: ",.2f" },
        { table: this.yearlyGeneratorCapacityTable, format: ",.2f" },
        this.selectedTab === "systemLevel"
          ? {
              table: this.yearlyGenerationByNodeTable,
              format: ",.2f",
            }
          : null,
        this.selectedTab === "systemLevel"
          ? {
              table: this.yearlyDemandByNodeTable,
              format: ",.2f",
            }
          : null,
        { table: this.yearlyFuelPricesTable, format: ",.0f" },
        { table: this.yearlyGenerationPerFuelTypesTable, format: ",.2f" },
        { table: this.yearlyStorageTable, format: ",.2f" },
        { table: this.yearlyEmmissionsTable, format: ",.2f" },
        { table: this.yearlyTotalGenerationCostByFuelTable, format: ",.0f" },
      ].filter((options) => options !== null && options.table !== null);
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    plur,
    formatNumber,

    allGeneratorOutputs(year) {
      if (!this.yearlyOutputs[year]) {
        return [];
      }
      return [
        ...this.yearlyOutputs[year].renewableGenerators,
        ...this.yearlyOutputs[year].hydroGenerators,
        ...this.yearlyOutputs[year].thermalGenerators,
      ];
    },

    yearlyFuelTypeData(key) {
      return this.simulationYearArray.reduce((accYear, year) => {
        if (this.allGeneratorOutputs(year).length === 0) {
          [...new Set(Object.values(this.generatorTypes))].forEach((fuelType) => {
            accYear[fuelType] = [...(accYear[fuelType] || []), "-"];
          });
        } else {
          Object.entries(
            this.allGeneratorOutputs(year).reduce((accGenerators, generator) => {
              const fuelType = this.generatorTypes[generator.id]; // this.caseInputs.generators[generator.id].source
              return {
                ...accGenerators,
                [fuelType]: (accGenerators[fuelType] || 0) + generator[key],
              };
            }, {})
          ).forEach(([fuelType, totalDischarge], _) => {
            accYear[fuelType] = [...(accYear[fuelType] || []), totalDischarge];
          });
        }
        return accYear;
      }, {});
    },
    groupGeneratorsByNode(allGenerators) {
      return allGenerators.reduce((acc, generator) => {
        if (!acc[generator.nodeID]) {
          acc[generator.nodeID] = [];
        }
        acc[generator.nodeID].push(generator);
        return acc;
      }, {});
    },
    getNodeDemand(yearlyDemands) {
      return yearlyDemands.reduce((acc, demand) => {
        acc[demand.nodeID] = demand.demand;
        return acc;
      }, {});
    },
    calculateGeneration(nodeGenerators) {
      return nodeGenerators.reduce((total, generator) => total + generator.generation, 0);
    },
    yearlyTableData(calculateValue, headerTitle) {
      const yearlyData = this.simulationYearArray.reduce((acc, year) => {
        const allGenerators = this.allGeneratorOutputs(year);

        if (allGenerators.length === 0) {
          this.nodes.forEach((node) => {
            acc[node.name] = [...(acc[node.name] || []), "-"];
          });
        } else {
          const nodeToGenerators = this.groupGeneratorsByNode(allGenerators);
          const nodeToDemand = this.getNodeDemand(this.yearlyOutputs[year].demands);

          this.nodes.forEach((node) => {
            const generators = nodeToGenerators[node.id] || [];
            const generation = this.calculateGeneration(generators);
            const demand = nodeToDemand[node.id] || 0;

            acc[node.name] = [...(acc[node.name] || []), calculateValue(generation, demand)];
          });
        }
        return acc;
      }, {});

      const dataToRows = Object.entries(yearlyData).map(([key, value]) => [
        `${key} (GWh)`,
        ...value,
      ]);

      return {
        header: [headerTitle, ...this.simulationYearArray],
        rows: dataToRows,
      };
    },
  },
};
</script>

<style></style>
